import {useEffect,useMemo,useState} from 'react';
import {Link,useNavigate} from 'react-router-dom';
import {getRepository} from '../data/repository';
import type {DashboardSummary,Document,FinancialYear,Profile} from '../types/vault';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {formatAUD,formatAustralianCalendarDate,formatFY} from '../utils/clipboard';
import {ModalDrawer} from '../components/ModalDrawer';
import {FinancialYearForm} from '../components/FinancialYearForm';

const {
  FiAlertCircle,
  FiBarChart2,
  FiCalendar,
  FiChevronRight,
  FiDatabase,
  FiFileText,
  FiHelpCircle,
  FiPieChart,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiTrendingDown,
  FiTrendingUp,
  FiUploadCloud,
  FiUsers,
}=FiIcons;

type ImportantDate={
  id: string;
  date: string;
  label: string;
  profileId: string;
  financialYearId: string;
};

type TrendPoint={
  endingYear: number;
  grossIncome: number;
  taxableIncome: number;
  taxWithheld: number;
};

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <=0) return '0 B';
  if (bytes < 1024) return Math.round(bytes) + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

function formatCompactAUD(value: number): string {
  const abs=Math.abs(value);
  const prefix=value < 0 ? '-' : '';
  if (abs >=1000000) return prefix + '$' + (abs / 1000000).toFixed(abs >=10000000 ? 0 : 1) + 'm';
  if (abs >=1000) return prefix + '$' + Math.round(abs / 1000) + 'k';
  return prefix + '$' + Math.round(abs);
}

function daysFromNow(value: string): number | null {
  const parsed=new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const today=new Date();
  const start=new Date(today.getFullYear(),today.getMonth(),today.getDate()).getTime();
  const target=new Date(parsed.getFullYear(),parsed.getMonth(),parsed.getDate()).getTime();
  return Math.round((target - start) / 86400000);
}

function greetingForNow(): string {
  const hour=new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function Dashboard() {
  const [summary,setSummary]=useState<DashboardSummary | null>(null);
  const [profiles,setProfiles]=useState<Profile[]>([]);
  const [years,setYears]=useState<FinancialYear[]>([]);
  const [documents,setDocuments]=useState<Document[]>([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState<string | null>(null);
  const [addingYearForProfile,setAddingYearForProfile]=useState<string | null>(null);
  const navigate=useNavigate();
  const currentUser=getRepository().getCurrentUser();
  const role=currentUser?.role || 'read_only';
  const canWrite=role==='owner' || role==='member';

  const loadSummary=()=> {
    setLoading(true);
    setError(null);
    const repo=getRepository();
    Promise.all([
      repo.getDashboardSummary(),
      repo.listProfiles(),
      repo.listFinancialYears(),
      repo.listDocuments({archived:false}),
    ])
      .then(([dashboard,allProfiles,allYears,activeDocuments])=> {
        setSummary(dashboard);
        setProfiles(allProfiles);
        setYears(allYears);
        setDocuments(activeDocuments);
      })
      .catch(err=> setError(err instanceof Error ? err.message : 'Unable to load dashboard data'))
      .finally(()=> setLoading(false));
  };

  useEffect(()=> {
    loadSummary();
  },[]);

  const handleSaveYear=async (year: FinancialYear)=> {
    await getRepository().saveFinancialYear(year);
    setAddingYearForProfile(null);
    loadSummary();
  };

  const activeProfiles=useMemo(()=> profiles.filter(profile=> !profile.archived),[profiles]);
  const activeProfileIds=useMemo(()=> new Set(activeProfiles.map(profile=> profile.id)),[activeProfiles]);
  const activeYears=useMemo(
    ()=> years.filter(year=> !year.archived && activeProfileIds.has(year.profileId)),
    [years,activeProfileIds]
  );
  const activeDocuments=useMemo(
    ()=> documents.filter(doc=> !doc.archived && activeProfileIds.has(doc.profileId)),
    [documents,activeProfileIds]
  );

  const trendData=useMemo<TrendPoint[]>(()=> {
    const grouped=new Map<number,TrendPoint>();
    activeYears.forEach(year=> {
      const existing=grouped.get(year.endingYear) || {
        endingYear: year.endingYear,
        grossIncome: 0,
        taxableIncome: 0,
        taxWithheld: 0,
      };
      existing.grossIncome += year.grossIncome || 0;
      existing.taxableIncome += year.taxableIncome || 0;
      existing.taxWithheld += year.taxWithheld || 0;
      grouped.set(year.endingYear,existing);
    });
    return Array.from(grouped.values())
      .sort((a,b)=> a.endingYear - b.endingYear)
      .slice(-7);
  },[activeYears]);

  const importantDates=useMemo<ImportantDate[]>(()=> {
    const items: ImportantDate[]=[];
    activeYears.forEach(year=> {
      if (year.lodgementDate) {
        items.push({
          id: year.id + '-lodgement',
          date: year.lodgementDate,
          label: formatFY(year.endingYear) + ' Lodgement',
          profileId: year.profileId,
          financialYearId: year.id,
        });
      }
      if (year.assessmentDate) {
        items.push({
          id: year.id + '-assessment',
          date: year.assessmentDate,
          label: formatFY(year.endingYear) + ' Assessment',
          profileId: year.profileId,
          financialYearId: year.id,
        });
      }
    });
    return items
      .sort((a,b)=> {
        const aDays=daysFromNow(a.date);
        const bDays=daysFromNow(b.date);
        if (aDays ==null && bDays ==null) return 0;
        if (aDays ==null) return 1;
        if (bDays ==null) return -1;
        const aFuture=aDays >=0;
        const bFuture=bDays >=0;
        if (aFuture !==bFuture) return aFuture ? -1 : 1;
        return aFuture ? aDays - bDays : bDays - aDays;
      })
      .slice(0,4);
  },[activeYears]);

  if (loading) {
    return (
      <div className="space-y-5 animate-pulse">
        <div className="h-16 bg-slate-200 rounded-2xl" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
          {[0,1,2,3].map(item=> <div key={item} className="h-28 bg-slate-200 rounded-2xl" />)}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <div className="xl:col-span-3 h-80 bg-slate-200 rounded-2xl" />
          <div className="h-80 bg-slate-200 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4 text-center">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-400" />
        <h2 className="text-lg font-semibold text-slate-900">Unable to Load Dashboard</h2>
        <p className="text-slate-500 max-w-md">{error}</p>
        <button onClick={loadSummary} className="inline-flex items-center px-4 py-2 bg-[#0B2138] text-white rounded-lg hover:bg-[#173B61] focus:outline-none focus:ring-2 focus:ring-[#0F766E]">
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" /> Retry
        </button>
      </div>
    );
  }

  const archivedProfiles=profiles.filter(profile=> profile.archived).length;
  const totalStorage=activeDocuments.reduce((sum,doc)=> sum + (doc.fileSize || 0),0);
  const totalGross=activeYears.reduce((sum,year)=> sum + (year.grossIncome || 0),0);
  const totalTaxable=activeYears.reduce((sum,year)=> sum + (year.taxableIncome || 0),0);
  const totalWithheld=activeYears.reduce((sum,year)=> sum + (year.taxWithheld || 0),0);
  const firstYear=trendData[0]?.endingYear;
  const lastYear=trendData[trendData.length - 1]?.endingYear;

  const latestTrend=trendData[trendData.length - 1];
  const previousTrend=trendData[trendData.length - 2];
  const trendDelta=latestTrend && previousTrend ? latestTrend.taxableIncome - previousTrend.taxableIncome : null;
  const trendPercent=trendDelta !=null && previousTrend.taxableIncome !==0
    ? (trendDelta / Math.abs(previousTrend.taxableIncome)) * 100
    : null;

  const insightTitle=trendDelta ==null
    ? 'Building your history'
    : trendDelta > 0
      ? 'Taxable income increased'
      : trendDelta < 0
        ? 'Taxable income decreased'
        : 'Taxable income is steady';
  const insightText=trendPercent ==null
    ? 'Add more financial years to build a clearer long-term trend.'
    : 'Latest recorded taxable income is ' + Math.abs(trendPercent).toFixed(1) + '% ' + (trendPercent >=0 ? 'higher' : 'lower') + ' than the prior recorded year.';

  const storageGroups=[
    {
      label:'PDF',
      bytes:activeDocuments.filter(doc=> doc.mimeType==='application/pdf').reduce((sum,doc)=> sum + (doc.fileSize || 0),0),
      color:'#102D4C',
    },
    {
      label:'Images',
      bytes:activeDocuments.filter(doc=> doc.mimeType.startsWith('image/')).reduce((sum,doc)=> sum + (doc.fileSize || 0),0),
      color:'#8B5CF6',
    },
    {
      label:'Spreadsheets',
      bytes:activeDocuments.filter(doc=> /spreadsheet|excel|csv/.test(doc.mimeType)).reduce((sum,doc)=> sum + (doc.fileSize || 0),0),
      color:'#B98937',
    },
  ];
  const knownStorage=storageGroups.reduce((sum,item)=> sum + item.bytes,0);
  storageGroups.push({
    label:'Other',
    bytes:Math.max(0,totalStorage - knownStorage),
    color:'#94A3B8',
  });

  let cumulativePercent=0;
  const storageGradient=totalStorage > 0
    ? 'conic-gradient(' + storageGroups.map(item=> {
        const start=cumulativePercent;
        cumulativePercent += (item.bytes / totalStorage) * 100;
        return item.color + ' ' + start.toFixed(2) + '% ' + cumulativePercent.toFixed(2) + '%';
      }).join(', ') + ')'
    : 'conic-gradient(#E2E8F0 0 100%)';

  const chartWidth=800;
  const chartHeight=270;
  const plotLeft=64;
  const plotRight=775;
  const plotTop=28;
  const plotBottom=215;
  const maxChartValue=Math.max(
    1,
    ...trendData.flatMap(point=> [point.grossIncome,point.taxableIncome,point.taxWithheld])
  ) * 1.12;
  const chartX=(index: number)=> trendData.length <=1
    ? (plotLeft + plotRight) / 2
    : plotLeft + ((plotRight - plotLeft) * index) / (trendData.length - 1);
  const chartY=(value: number)=> plotBottom - ((plotBottom - plotTop) * value) / maxChartValue;
  const grossPath=trendData.map((point,index)=> chartX(index) + ',' + chartY(point.grossIncome)).join(' ');
  const taxablePath=trendData.map((point,index)=> chartX(index) + ',' + chartY(point.taxableIncome)).join(' ');
  const withheldPath=trendData.map((point,index)=> chartX(index) + ',' + chartY(point.taxWithheld)).join(' ');

  const profileName=(profileId: string)=> summary.profileCards.find(card=> card.profile.id===profileId)?.profile.displayName || 'Unknown profile';
  const userFirstName=(currentUser?.name || currentUser?.email || 'there').split(/[\s@]/)[0];

  return (
    <div className="space-y-5 md:space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-950">
            {greetingForNow()}, {userFirstName}
          </h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">Here&apos;s an overview of your financial world.</p>
        </div>
        <div className="text-xs md:text-sm text-slate-500">
          {new Intl.DateTimeFormat('en-AU',{weekday:'long',day:'numeric',month:'long',year:'numeric'}).format(new Date())}
        </div>
      </header>

      <section className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        <button onClick={()=> navigate('/profiles')} className="text-left bg-white rounded-2xl border border-violet-100 shadow-sm p-4 md:p-5 hover:shadow-md hover:border-violet-200 transition-all focus:outline-none focus:ring-2 focus:ring-[#B98937]">
          <div className="flex items-start justify-between gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center">
              <SafeIcon icon={FiUsers} className="w-5 h-5" />
            </div>
            <SafeIcon icon={FiChevronRight} className="w-4 h-4 text-violet-400" />
          </div>
          <p className="mt-4 text-xs text-slate-500">Total Profiles</p>
          <p className="text-2xl font-bold text-slate-950">{activeProfiles.length}</p>
          <p className="text-[11px] text-slate-400 mt-1">{archivedProfiles} archived</p>
        </button>

        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-4 md:p-5">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <SafeIcon icon={FiCalendar} className="w-5 h-5" />
          </div>
          <p className="mt-4 text-xs text-slate-500">Financial Years</p>
          <p className="text-2xl font-bold text-slate-950">{activeYears.length}</p>
          <p className="text-[11px] text-slate-400 mt-1">
            {firstYear && lastYear ? formatFY(firstYear) + ' – ' + formatFY(lastYear) : 'No years recorded'}
          </p>
        </div>

        <button onClick={()=> navigate('/documents')} className="text-left bg-white rounded-2xl border border-blue-100 shadow-sm p-4 md:p-5 hover:shadow-md hover:border-blue-200 transition-all focus:outline-none focus:ring-2 focus:ring-[#B98937]">
          <div className="flex items-start justify-between gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
              <SafeIcon icon={FiFileText} className="w-5 h-5" />
            </div>
            <SafeIcon icon={FiChevronRight} className="w-4 h-4 text-blue-400" />
          </div>
          <p className="mt-4 text-xs text-slate-500">Total Documents</p>
          <p className="text-2xl font-bold text-slate-950">{activeDocuments.length}</p>
          <p className="text-[11px] text-slate-400 mt-1">{summary.recentDocuments.length} recent in dashboard</p>
        </button>

        <div className="bg-white rounded-2xl border border-amber-100 shadow-sm p-4 md:p-5">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
            <SafeIcon icon={FiDatabase} className="w-5 h-5" />
          </div>
          <p className="mt-4 text-xs text-slate-500">Storage Used</p>
          <p className="text-2xl font-bold text-slate-950">{formatFileSize(totalStorage)}</p>
          <p className="text-[11px] text-slate-400 mt-1">Across active vault files</p>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="xl:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 md:px-6 pt-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiBarChart2} className="w-5 h-5 text-violet-700" />
                <h2 className="text-lg font-semibold text-slate-950">Tax Position Over Time</h2>
              </div>
              <p className="text-xs md:text-sm text-slate-500 mt-1">Gross income, taxable income and tax withheld across recorded financial years.</p>
            </div>
            <span className="self-start rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">All active profiles</span>
          </div>

          <div className="px-4 md:px-6 pt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-600">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#102D4C]" />Gross Income</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#B98937]" />Taxable Income</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-violet-500" />Tax Withheld</span>
          </div>

          {trendData.length > 0 ? (
            <div className="px-2 md:px-4 pb-3 overflow-hidden">
              <svg viewBox={'0 0 ' + chartWidth + ' ' + chartHeight} className="w-full h-auto min-h-[220px]" role="img" aria-label="Tax position trend chart">
                {[0,0.25,0.5,0.75,1].map((fraction)=> {
                  const y=plotBottom - (plotBottom - plotTop) * fraction;
                  return (
                    <g key={fraction}>
                      <line x1={plotLeft} y1={y} x2={plotRight} y2={y} stroke="#E2E8F0" strokeWidth="1" />
                      <text x={plotLeft - 10} y={y + 4} textAnchor="end" fontSize="10" fill="#94A3B8">
                        {formatCompactAUD(maxChartValue * fraction)}
                      </text>
                    </g>
                  );
                })}
                <polyline points={grossPath} fill="none" stroke="#102D4C" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points={taxablePath} fill="none" stroke="#B98937" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points={withheldPath} fill="none" stroke="#8B5CF6" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                {trendData.map((point,index)=> {
                  const x=chartX(index);
                  const grossY=chartY(point.grossIncome);
                  const taxableY=chartY(point.taxableIncome);
                  const withheldY=chartY(point.taxWithheld);
                  return (
                    <g key={point.endingYear}>
                      <circle cx={x} cy={grossY} r="4.5" fill="#102D4C" />
                      <circle cx={x} cy={taxableY} r="4.5" fill="#B98937" />
                      <circle cx={x} cy={withheldY} r="4.5" fill="#8B5CF6" />
                      <text x={x} y={grossY - 10} textAnchor="middle" fontSize="9" fontWeight="600" fill="#102D4C">{formatCompactAUD(point.grossIncome)}</text>
                      <text x={x} y={taxableY + 16} textAnchor="middle" fontSize="9" fontWeight="600" fill="#A66912">{formatCompactAUD(point.taxableIncome)}</text>
                      <text x={x} y={Math.max(plotTop + 10,withheldY - 9)} textAnchor="middle" fontSize="9" fontWeight="600" fill="#7C3AED">{formatCompactAUD(point.taxWithheld)}</text>
                      <text x={x} y={plotBottom + 24} textAnchor="middle" fontSize="10" fill="#64748B">{formatFY(point.endingYear)}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          ) : (
            <div className="px-6 py-16 text-center text-sm text-slate-500">Add financial years to build your tax trend.</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-slate-100">
            {[
              ['Total Gross Income',totalGross,'bg-blue-50 text-blue-700'],
              ['Total Taxable Income',totalTaxable,'bg-amber-50 text-amber-700'],
              ['Total Tax Withheld',totalWithheld,'bg-violet-50 text-violet-700'],
            ].map(([label,value,tone])=> (
              <div key={String(label)} className="p-4 md:p-5 border-b sm:border-b-0 sm:border-r last:border-r-0 border-slate-100">
                <div className={'inline-flex rounded-lg px-2 py-1 text-[10px] font-semibold ' + String(tone)}>{String(label)}</div>
                <p className="mt-2 text-xl font-bold text-slate-950">{formatAUD(Number(value))}</p>
              </div>
            ))}
          </div>
        </div>

        <aside className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6 flex flex-col">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center">
              <SafeIcon icon={trendDelta !=null && trendDelta < 0 ? FiTrendingDown : FiTrendingUp} className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-slate-950">Quick Insight</h2>
          </div>
          <div className="mt-6">
            <p className="text-xl font-semibold text-slate-900">{insightTitle}</p>
            <p className="text-sm text-slate-500 mt-2 leading-6">{insightText}</p>
          </div>
          <div className="mt-auto pt-6">
            <div className="rounded-xl bg-[#FFF7E6] border border-[#E3C684] p-4">
              <p className="text-xs font-semibold text-[#9B702A] uppercase tracking-wide">Vault principle</p>
              <p className="text-sm text-slate-700 mt-2 leading-5">A clear financial history makes every future review easier.</p>
            </div>
          </div>
        </aside>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-950">Recent Documents</h2>
            <Link to="/documents" className="text-xs font-medium text-violet-700 hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {summary.recentDocuments.slice(0,4).map(doc=> (
              <button key={doc.id} onClick={()=> navigate('/documents')} className="w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                    <SafeIcon icon={FiFileText} className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">{doc.title}</p>
                    <p className="text-[11px] text-slate-500 truncate">{profileName(doc.profileId)} • {formatFileSize(doc.fileSize)}</p>
                  </div>
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">{new Date(doc.updated || doc.created).toLocaleDateString('en-AU',{day:'2-digit',month:'short'})}</span>
                </div>
              </button>
            ))}
            {summary.recentDocuments.length===0 && (
              <div className="px-5 py-8 text-center text-sm text-slate-500">No documents yet.</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-950">Important Dates</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {importantDates.map(item=> {
              const remaining=daysFromNow(item.date);
              return (
                <button key={item.id} onClick={()=> navigate('/profiles/' + item.profileId + '/financial-years/' + item.financialYearId)} className="w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-700 flex flex-col items-center justify-center shrink-0">
                      <span className="text-sm font-bold leading-4">{new Date(item.date).getDate()}</span>
                      <span className="text-[9px] font-semibold uppercase">{new Date(item.date).toLocaleDateString('en-AU',{month:'short'})}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{item.label}</p>
                      <p className="text-[11px] text-slate-500 truncate">{profileName(item.profileId)} • {formatAustralianCalendarDate(item.date)}</p>
                    </div>
                    {remaining !=null && (
                      <span className={(remaining >=0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500') + ' rounded-full px-2 py-1 text-[10px] font-medium whitespace-nowrap'}>
                        {remaining ===0 ? 'Today' : remaining > 0 ? remaining + ' days' : Math.abs(remaining) + 'd ago'}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
            {importantDates.length===0 && (
              <div className="px-5 py-8 text-center text-sm text-slate-500">No lodgement or assessment dates recorded.</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-950">Profiles at a Glance</h2>
            <Link to="/profiles" className="text-xs font-medium text-violet-700 hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {summary.profileCards.slice(0,4).map(card=> {
              const yearCount=activeYears.filter(year=> year.profileId===card.profile.id).length;
              return (
                <div key={card.profile.id} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50">
                  <button onClick={()=> navigate('/profiles/' + card.profile.id)} className="flex items-center min-w-0 flex-1 text-left">
                    <div className="w-9 h-9 rounded-full bg-[#102D4C] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                      {card.profile.displayName.split(' ').map(part=> part[0]).slice(0,2).join('').toUpperCase()}
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{card.profile.displayName}</p>
                      <p className="text-[11px] text-slate-500">{yearCount} years • {card.documentCount} documents</p>
                    </div>
                  </button>
                  {canWrite && (
                    <button onClick={()=> setAddingYearForProfile(card.profile.id)} className="w-8 h-8 rounded-lg text-violet-700 hover:bg-violet-50 flex items-center justify-center" aria-label={'Add financial year for ' + card.profile.displayName}>
                      <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
            {summary.profileCards.length===0 && (
              <div className="px-5 py-8 text-center text-sm text-slate-500">No active profiles yet.</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-950">Storage &amp; Security</h2>
            <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="mt-5 flex items-center gap-4">
            <div className="relative w-24 h-24 rounded-full shrink-0" style={{background:storageGradient}}>
              <div className="absolute inset-3 rounded-full bg-white flex flex-col items-center justify-center text-center">
                <span className="text-sm font-bold text-slate-950">{formatFileSize(totalStorage)}</span>
                <span className="text-[9px] text-slate-400">stored</span>
              </div>
            </div>
            <div className="space-y-2 min-w-0 flex-1">
              {storageGroups.map(item=> {
                const pct=totalStorage > 0 ? Math.round((item.bytes / totalStorage) * 100) : 0;
                return (
                  <div key={item.label} className="flex items-center gap-2 text-[11px]">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{backgroundColor:item.color}} />
                    <span className="text-slate-500 flex-1 truncate">{item.label}</span>
                    <span className="font-medium text-slate-700">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-100 p-3 flex gap-3">
            <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-emerald-800">Protected vault access</p>
              <p className="text-[11px] text-emerald-700 mt-1">Documents remain behind authenticated vault access.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5">
        <div className="flex flex-col xl:flex-row xl:items-center gap-4">
          <div className="xl:w-56 shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center">
                <SafeIcon icon={FiPlus} className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-950">Quick Actions</h2>
                <p className="text-[11px] text-slate-500">Common tasks, one tap away.</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 md:gap-3 flex-1">
            {canWrite && (
              <button onClick={()=> navigate('/profiles')} className="rounded-xl border border-slate-200 p-3 text-left hover:border-violet-200 hover:bg-violet-50/40 transition-colors">
                <SafeIcon icon={FiUsers} className="w-4 h-4 text-violet-700" />
                <p className="text-xs font-semibold text-slate-800 mt-2">Add Profile</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Manage profiles</p>
              </button>
            )}
            {canWrite && (
              <button onClick={()=> navigate('/profiles')} className="rounded-xl border border-slate-200 p-3 text-left hover:border-amber-200 hover:bg-amber-50/40 transition-colors">
                <SafeIcon icon={FiCalendar} className="w-4 h-4 text-amber-700" />
                <p className="text-xs font-semibold text-slate-800 mt-2">Add Financial Year</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Choose a profile</p>
              </button>
            )}
            {canWrite && (
              <button onClick={()=> navigate('/documents')} className="rounded-xl border border-slate-200 p-3 text-left hover:border-blue-200 hover:bg-blue-50/40 transition-colors">
                <SafeIcon icon={FiUploadCloud} className="w-4 h-4 text-blue-700" />
                <p className="text-xs font-semibold text-slate-800 mt-2">Upload Document</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Open documents</p>
              </button>
            )}
            <button onClick={()=> navigate('/reports')} className="rounded-xl border border-slate-200 p-3 text-left hover:border-emerald-200 hover:bg-emerald-50/40 transition-colors">
              <SafeIcon icon={FiPieChart} className="w-4 h-4 text-emerald-700" />
              <p className="text-xs font-semibold text-slate-800 mt-2">Generate Report</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Export insights</p>
            </button>
            <button onClick={()=> navigate('/settings/guide')} className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 text-left hover:bg-violet-100/70 transition-colors">
              <SafeIcon icon={FiHelpCircle} className="w-4 h-4 text-violet-700" />
              <p className="text-xs font-semibold text-slate-800 mt-2">Help &amp; Guide</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Learn how MFV works</p>
            </button>
          </div>
        </div>
      </section>

      <ModalDrawer isOpen={!!addingYearForProfile} onClose={()=> setAddingYearForProfile(null)} title="Add Financial Year">
        {addingYearForProfile && (
          <FinancialYearForm profileId={addingYearForProfile} onSave={handleSaveYear} onCancel={()=> setAddingYearForProfile(null)} />
        )}
      </ModalDrawer>
    </div>
  );
}